// Cash Payments Service
// خدمة سندات الصرف النقدي (CP)

import api from './api';
import { 
  VoucherDto, 
  CreateVoucherDto, 
  UpdateVoucherDto 
} from '../types/vouchers.types';

const BASE_URL = '/vouchers';

export const cashPaymentsService = {
  /**
   * Get all cash payment vouchers (CP)
   * الحصول على جميع سندات الصرف النقدي
   */
  getAll: async (): Promise<VoucherDto[]> => {
    const response = await api.get<{ data: VoucherDto[] }>(`${BASE_URL}/type/CP`);
    return response.data.data;
  },

  /**
   * Get cash payment voucher by ID
   * الحصول على سند صرف نقدي بالمعرف
   */
  getById: async (id: number): Promise<VoucherDto> => {
    const response = await api.get<{ data: VoucherDto }>(`${BASE_URL}/${id}`);
    return response.data.data;
  },

  /**
   * Get cash payment vouchers by date range
   * الحصول على سندات الصرف النقدي حسب نطاق التاريخ
   */
  getByDateRange: async (startDate: string, endDate: string): Promise<VoucherDto[]> => {
    const response = await api.get<{ data: VoucherDto[] }>(
      `${BASE_URL}/daterange`,
      {
        params: {
          startDate,
          endDate,
          type: 'CP'
        }
      }
    );
    return response.data.data;
  },

  /**
   * Get cash payment vouchers by status
   * الحصول على سندات الصرف النقدي حسب الحالة
   */
  getByStatus: async (status: 'Draft' | 'Posted'): Promise<VoucherDto[]> => {
    const response = await api.get<{ data: VoucherDto[] }>(
      `${BASE_URL}/status/${status}`,
      {
        params: { type: 'CP' }
      }
    );
    return response.data.data;
  },

  /**
   * Create new cash payment voucher
   * إنشاء سند صرف نقدي جديد
   */
  create: async (dto: CreateVoucherDto): Promise<VoucherDto> => {
    // Ensure voucherType is CP
    const payload = {
      ...dto,
      voucherType: 'CP' as const,
      currencyCode: dto.currencyCode || 'KWD',
      exchangeRate: dto.exchangeRate || 1
    };

    const response = await api.post<{ data: VoucherDto }>(`${BASE_URL}`, payload);
    return response.data.data;
  },

  /**
   * Update cash payment voucher (Draft only)
   * تحديث سند صرف نقدي (المسودات فقط)
   */
  update: async (id: number, dto: UpdateVoucherDto): Promise<VoucherDto> => {
    const response = await api.put<{ data: VoucherDto }>(`${BASE_URL}/${id}`, dto);
    return response.data.data;
  },

  /**
   * Delete cash payment voucher (Draft only)
   * حذف سند صرف نقدي (المسودات فقط)
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Post cash payment voucher (transfer to General Ledger)
   * ترحيل سند الصرف النقدي (نقل إلى الأستاذ العام)
   */
  post: async (id: number): Promise<VoucherDto> => {
    const response = await api.post<{ data: VoucherDto }>(`${BASE_URL}/${id}/post`);
    return response.data.data;
  },

  /**
   * Unpost cash payment voucher (remove from General Ledger)
   * إلغاء ترحيل سند الصرف النقدي (حذف من الأستاذ العام)
   */
  unpost: async (id: number): Promise<VoucherDto> => {
    const response = await api.post<{ data: VoucherDto }>(`${BASE_URL}/${id}/unpost`);
    return response.data.data;
  },

  /**
   * Generate next voucher number
   * توليد رقم السند التالي
   */
  generateVoucherNumber: async (): Promise<string> => {
    // This will be generated by backend, but we can call entry number service
    const currentYear = new Date().getFullYear();
    const response = await api.get<{ data: string }>(
      `/entry-numbering/generate`,
      {
        params: {
          entryTypeCode: 'CP',
          fiscalYear: currentYear
        }
      }
    );
    return response.data.data;
  }
};

export default cashPaymentsService;